import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, Send, Trash2, Download, CheckCircle2, XCircle, Copy } from "lucide-react";
import { PageHeader, StatusBadge, StatusTabs, CardActionMenu, formatDate } from "@/lib/ui";
import { saveOrShareBlob, fetchAndSave } from "@/lib/downloads";
import { downloadCsv, iso, money } from "@/lib/csvExport";

const STATUS_TINT = {
  draft: "bg-[#F5F1EA] text-[#57534E]",
  sent: "bg-[#FDE9D8] text-[#B85C1F]",
  paid: "bg-[#DCF2E1] text-[#1F6B36]",
  void: "bg-[#F0E0E0] text-[#7A2323]",
  overdue: "bg-[#FDE0DC] text-[#B02222]",
};

function centsToStr(c) { return `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Invoices() {
  const { currentOrgId, user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blank());
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");
  const [finalizedFilter, setFinalizedFilter] = useState(params.get("finalized") || "");

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (statusFilter && statusFilter !== "all") next.set("status", statusFilter); else next.delete("status");
    if (finalizedFilter) next.set("finalized", finalizedFilter); else next.delete("finalized");
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q, statusFilter, finalizedFilter]);

  function blank() {
    return {
      client_id: "none",
      currency: "usd",
      tax_percent: 0,
      issued_at: new Date().toISOString().slice(0, 10),
      due_date: "",
      notes: "",
      terms: "Payment due within 14 days of invoice date.",
      items: [{ description: "", quantity: 1, unit_price_cents: 0 }],
      amount_paid_cents: 0,
    };
  }

  const load = async () => {
    if (!currentOrgId) return;
    setLoading(true);
    const [inv, cli] = await Promise.all([
      apiFetch(`/invoices?organization_id=${currentOrgId}`).catch(() => ({ invoices: [] })),
      supabase.from("clients").select("id,first_name,last_name,email").eq("organization_id", currentOrgId).order("first_name"),
    ]);
    setRows(inv.invoices || []);
    setClients(cli.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [currentOrgId]);

  // Deep-link: open ?id=xxx invoice automatically once rows load
  useEffect(() => {
    const id = params.get("id");
    if (!id || !rows.length || open) return;
    const row = rows.find((r) => r.id === id);
    if (row) { openEdit(row); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [rows, params.get("id")]);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price_cents) || 0), 0);
    const tax = Math.round(subtotal * (Number(form.tax_percent) || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items, form.tax_percent]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price_cents: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, key, val) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, [key]: val } : it) });

  const openNew = (prefillClientId = null) => {
    setEditId(null);
    const b = blank();
    if (prefillClientId) b.client_id = prefillClientId;
    setForm(b);
    setOpen(true);
  };

  // Deep-link: /app/invoices?new=1&client_id=xxx opens the new-invoice dialog
  // prefilled with the client so users don't have to search again.
  useEffect(() => {
    if (params.get("new") === "1") {
      openNew(params.get("client_id") || null);
      params.delete("new"); params.delete("client_id");
      setParams(params, { replace: true });
    }
    /* eslint-disable-next-line */
  }, []);
  const openEdit = async (row) => {
    const inv = await apiFetch(`/invoices/${row.id}`);
    setEditId(row.id);
    setForm({
      client_id: inv.client_id || "none",
      currency: inv.currency, tax_percent: Number(inv.tax_percent) || 0,
      issued_at: inv.issued_at || new Date().toISOString().slice(0, 10),
      due_date: inv.due_date || "",
      notes: inv.notes || "", terms: inv.terms || "",
      items: (inv.items && inv.items.length ? inv.items : [{ description: "", quantity: 1, unit_price_cents: 0 }])
        .map((it) => ({
          description: it.description, quantity: Number(it.quantity) || 1,
          unit_price_cents: Number(it.unit_price_cents) || 0,
        })),
      amount_paid_cents: Number(inv.amount_paid_cents) || 0,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.items.length || form.items.some((it) => !it.description.trim())) {
      return toast.error("Every line item needs a description");
    }
    setSaving(true);
    const body = {
      organization_id: currentOrgId,
      client_id: form.client_id === "none" ? null : form.client_id,
      currency: form.currency, tax_percent: Number(form.tax_percent) || 0,
      issued_at: form.issued_at, due_date: form.due_date || null,
      notes: form.notes || null, terms: form.terms || null,
      items: form.items.map((it, idx) => ({
        description: it.description,
        quantity: Number(it.quantity) || 1,
        unit_price_cents: Math.round(Number(it.unit_price_cents) || 0),
        position: idx,
      })),
    };
    try {
      if (editId) await apiFetch(`/invoices/${editId}`, { method: "PATCH", body: JSON.stringify(body) });
      else await apiFetch("/invoices", { method: "POST", body: JSON.stringify(body) });
      toast.success(editId ? "Invoice updated" : "Invoice created");
      setOpen(false); setEditId(null); setForm(blank()); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const sendInvoice = async (row) => {
    if (!row.client_id) return toast.error("Attach a client with an email first");
    try {
      const r = await apiFetch(`/invoices/${row.id}/send`, { method: "POST", body: JSON.stringify({ send_email: true }) });
      if (r.email_sent) toast.success("Invoice sent — client emailed");
      else if (r.portal_link) {
        toast.success("Invoice marked sent. Copy the pay link below.");
        try { await navigator.clipboard.writeText(r.portal_link); toast.success("Pay link copied"); } catch {}
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  const markPaid = async (row) => {
    if (!window.confirm(`Mark ${row.invoice_number} as paid?`)) return;
    try { await apiFetch(`/invoices/${row.id}/mark-paid`, { method: "POST", body: "{}" }); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const voidInvoice = async (row) => {
    if (!window.confirm(`Void ${row.invoice_number}? Client will no longer be able to pay it.`)) return;
    try {
      const r = await apiFetch(`/invoices/${row.id}/void`, { method: "POST", body: "{}" });
      // Complete / Partial / Failed selector — never assume success from HTTP 200
      const sync = r?.external_sync;
      if (sync === "partial") {
        const failed = Object.entries(r?.sync_detail || {}).filter(([, v]) => v === "failed").map(([k]) => k.charAt(0).toUpperCase() + k.slice(1));
        toast.warning(`Invoice ${row.invoice_number} voided in ClientDesk VA, but ${failed.join(" and ") || "an external system"} did not sync. Please review it manually.`, { duration: 9000 });
      } else if (sync === "complete") {
        toast.success(`Invoice ${row.invoice_number} voided. All external operations succeeded.`);
      } else {
        toast.success(`Invoice ${row.invoice_number} voided.`);
      }
      load();
    } catch (e) {
      // Never expose raw Stripe / backend detail to the VA
      toast.error(`Could not void ${row.invoice_number}. No changes were committed.`);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.invoice_number}? This cannot be undone.`)) return;
    try { await apiFetch(`/invoices/${row.id}`, { method: "DELETE" }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const downloadPDF = async (row) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetchAndSave(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${row.id}/pdf`, {
        token: session.access_token,
        filename: `${row.invoice_number || row.id}.pdf`,
        options: { title: `Invoice ${row.invoice_number || ""}`, dialogTitle: "Save or share invoice" },
      });
      toast.success("Invoice PDF ready");
    } catch (e) { toast.error(e.message || "PDF failed"); }
  };

  const downloadAudit = async (row) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetchAndSave(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${row.id}/audit.csv`, {
        token: session.access_token,
        filename: `audit_${row.invoice_number || row.id}.csv`,
        options: { title: "Invoice audit history", dialogTitle: "Save or share audit CSV" },
      });
      toast.success("Audit history ready");
    } catch (e) { toast.error(e.message || "Audit export failed"); }
  };

  // Full status set — order chosen so most common statuses appear first.
  const STATUS_TABS = [
    { id: "all",     label: "All" },
    { id: "draft",   label: "Draft" },
    { id: "sent",    label: "Sent" },
    { id: "viewed",  label: "Viewed" },
    { id: "paid",    label: "Paid" },
    { id: "overdue", label: "Overdue" },
    { id: "void",    label: "Void" },
  ];
  const statusCounts = useMemo(() => {
    const counts = { all: rows.length };
    rows.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [rows]);
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (finalizedFilter === "missing") {
        const inScope = ["sent", "paid", "overdue", "void"].includes(r.status);
        if (!inScope || r.finalized_pdf_url) return false;
      }
      if (q) {
        const ql = q.toLowerCase();
        const clientName = r.clients ? `${r.clients.first_name} ${r.clients.last_name}`.toLowerCase() : "";
        if (!(r.invoice_number || "").toLowerCase().includes(ql) && !clientName.includes(ql)) return false;
      }
      return true;
    });
  }, [rows, q, statusFilter, finalizedFilter]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle="Create, send, and collect payments with Stripe."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" data-testid="inv-export-csv"
              disabled={filteredRows.length === 0}
              onClick={() => downloadCsv(
                `invoices-${iso(new Date())}`,
                [
                  { key: "invoice_number", label: "Invoice #" },
                  { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "" },
                  { key: "status", label: "Status" },
                  { label: "Currency", get: (r) => (r.currency || "usd").toUpperCase() },
                  { label: "Subtotal", get: (r) => money(r.subtotal_cents, r.currency || "usd") },
                  { label: "Tax %",   get: (r) => r.tax_percent || 0 },
                  { label: "Tax",     get: (r) => money(r.tax_cents, r.currency || "usd") },
                  { label: "Total",   get: (r) => money(r.total_cents, r.currency || "usd") },
                  { label: "Amount paid", get: (r) => money(r.amount_paid_cents, r.currency || "usd") },
                  { label: "Issued",  get: (r) => iso(r.issued_at) },
                  { label: "Due",     get: (r) => iso(r.due_date) },
                  { label: "Paid at", get: (r) => iso(r.paid_at) },
                  { label: "Voided at", get: (r) => iso(r.voided_at) },
                ],
                filteredRows,
              )}
            ><Download size={14} className="mr-1.5" /> Export CSV</Button>
            <Button data-testid="new-invoice-btn" onClick={() => openNew()} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
              <Plus size={16} className="mr-1.5" /> New invoice
            </Button>
          </div>
        }
      />

      <StatusTabs
        tabs={STATUS_TABS}
        value={statusFilter}
        onChange={setStatusFilter}
        counts={statusCounts}
        testidPrefix="inv-status-tab"
        right={
          <input
            data-testid="inv-search"
            placeholder="Search invoice # or client…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="hidden md:block bg-transparent border-0 outline-none text-sm px-3 min-w-[220px]"
          />
        }
      />

      {finalizedFilter === "missing" && (
        <div
          className="bg-[#FDDCDC] border border-[#F5B4B4] text-[#B02222] rounded-2xl px-4 py-3 flex items-center gap-3"
          data-testid="inv-finalized-filter-banner"
        >
          <span className="text-sm">
            Showing <b>{filteredRows.length}</b> invoice{filteredRows.length === 1 ? "" : "s"} that are issued but
            missing a stored authoritative PDF snapshot.
          </span>
          <button
            className="ml-auto text-xs px-3 py-1 rounded-full bg-white border border-[#F5B4B4] hover:bg-[#FEE9E9]"
            onClick={() => setFinalizedFilter("")}
            data-testid="inv-finalized-filter-clear"
          >
            Clear filter
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E8E2D9] rounded-2xl p-16 text-center">
          <FileText size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
          <p className="text-sm text-[#57534E] mb-4">No invoices yet. Create your first one.</p>
          <Button onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={14} className="mr-1.5" /> New invoice</Button>
        </div>
      ) : (
        <div className="bg-white border border-[#E8E2D9] rounded-2xl overflow-hidden">
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                <th className="text-left p-3">Number</th>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">Issued</th>
                <th className="text-left p-3">Due</th>
                <th className="text-right p-3">Total</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`invoice-row-${r.id}`} className="hover:bg-[#FAF8F5]">
                  <td className="p-3 font-medium">
                    <button
                      onClick={() => openEdit(r)}
                      className="text-[#B85C1F] hover:underline"
                      data-testid={`inv-number-link-${r.id}`}
                    >
                      {r.invoice_number}
                    </button>
                  </td>
                  <td className="p-3">
                    {r.clients ? (
                      <button
                        onClick={() => navigate(`/app/clients/${r.client_id}`)}
                        className="text-[#B85C1F] hover:underline"
                        data-testid={`inv-client-link-${r.id}`}
                      >
                        {r.clients.first_name} {r.clients.last_name}
                      </button>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-[#57534E]">{r.issued_at ? formatDate(r.issued_at) : "—"}</td>
                  <td className="p-3 text-[#57534E]">{r.due_date ? formatDate(r.due_date) : "—"}</td>
                  <td className="p-3 text-right font-medium">{centsToStr(r.total_cents)} <span className="text-xs text-[#A8A29E]">{r.currency.toUpperCase()}</span></td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_TINT[r.status] || "bg-[#F5F1EA]"}`}>
                      {r.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {r.status === "draft" && (
                        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}>Edit</Button>
                      )}
                      {r.status === "draft" && (
                        <Button size="sm" className="rounded-full h-8 bg-[#E8712C] hover:bg-[#C55A1F]" onClick={() => sendInvoice(r)} data-testid={`send-${r.id}`}>
                          <Send size={12} className="mr-1" /> Send
                        </Button>
                      )}
                      {r.status === "sent" && (
                        <Button size="sm" className="rounded-full h-8 bg-[#1F6B36] hover:bg-[#155024]" onClick={() => markPaid(r)} data-testid={`mark-paid-${r.id}`}>
                          <CheckCircle2 size={12} className="mr-1" /> Mark paid
                        </Button>
                      )}
                      {r.status === "sent" && (
                        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => sendInvoice(r)} title="Resend"><Send size={12} /></Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => downloadPDF(r)} title="Download PDF"><Download size={12} /></Button>
                      {r.status !== "paid" && r.status !== "void" && (
                        <Button size="sm" variant="outline" className="rounded-full h-8 text-[#B02222] border-[#F0C69A]" onClick={() => voidInvoice(r)} title="Void"><XCircle size={12} /></Button>
                      )}
                      {(r.status === "draft" || r.status === "void") && (
                        <Button size="sm" variant="outline" className="rounded-full h-8 text-[#B02222] border-[#F0C69A]" onClick={() => del(r)} title="Delete"><Trash2 size={12} /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile card list (<md) — whole card taps into edit view */}
          <div className="md:hidden divide-y divide-[#E8E2D9]">
            {filteredRows.map((r) => (
              <div key={r.id} className="flex items-center gap-1 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors" data-testid={`inv-card-${r.id}`}>
                <button
                  onClick={() => openEdit(r)}
                  className="flex-1 text-left px-4 py-4 min-w-0"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold">{r.invoice_number}</div>
                        <StatusBadge value={r.status} />
                      </div>
                      {r.clients && (
                        <div className="text-sm text-[#57534E] truncate mt-0.5">{r.clients.first_name} {r.clients.last_name}</div>
                      )}
                      <div className="text-xs text-[#A8A29E] mt-1">
                        Issued {r.issued_at ? formatDate(r.issued_at) : "—"}
                        {r.due_date ? ` · Due ${formatDate(r.due_date)}` : ""}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-base font-semibold">{centsToStr(r.total_cents)}</div>
                      <div className="text-[10px] uppercase text-[#A8A29E]">{r.currency}</div>
                    </div>
                  </div>
                </button>
                <CardActionMenu
                  testid={`inv-menu-${r.id}`}
                  actions={[
                    { label: r.status === "sent" ? "Resend" : "Send", icon: Send, onClick: () => sendInvoice(r), hidden: r.status === "paid" || r.status === "void", testid: `menu-send-${r.id}` },
                    { label: r.status === "sent" ? "Mark paid" : null, icon: CheckCircle2, onClick: () => markPaid(r), hidden: r.status !== "sent", testid: `menu-paid-${r.id}` },
                    { label: "Download PDF", icon: Download, onClick: () => downloadPDF(r), testid: `menu-pdf-${r.id}` },
                    { label: "Download audit history", icon: FileText, onClick: () => downloadAudit(r), testid: `menu-audit-${r.id}` },
                    { separator: true },
                    { label: "Void", icon: XCircle, destructive: true, confirmMessage: `Void invoice ${r.invoice_number}? Client will see it as cancelled.`, onClick: () => voidInvoice(r), hidden: r.status === "paid" || r.status === "void", testid: `menu-void-${r.id}` },
                    { label: "Delete", icon: Trash2, destructive: true, confirmMessage: `Delete invoice ${r.invoice_number}? This cannot be undone.`, onClick: () => del(r), hidden: !(r.status === "draft" || r.status === "void"), testid: `menu-delete-${r.id}` },
                  ]}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editId ? "Edit invoice" : "New invoice"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="inv-client" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No client (draft) —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">USD</SelectItem>
                    <SelectItem value="eur">EUR</SelectItem>
                    <SelectItem value="gbp">GBP</SelectItem>
                    <SelectItem value="cad">CAD</SelectItem>
                    <SelectItem value="aud">AUD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Issued</Label><Input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line items</Label>
                <Button size="sm" variant="outline" className="rounded-full" onClick={addItem} data-testid="add-item"><Plus size={12} className="mr-1" /> Add line</Button>
              </div>
              {/* Column headers */}
              <div className="hidden md:grid grid-cols-12 gap-2 px-1 mb-1 text-[10px] uppercase tracking-wider text-[#57534E]">
                <div className="col-span-5">Description</div>
                <div className="col-span-2 text-right">Qty / Hours</div>
                <div className="col-span-2 text-right">Rate</div>
                <div className="col-span-2 text-right">Line total</div>
                <div className="col-span-1" />
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => {
                  const q = Number(it.quantity) || 0;
                  const u = Number(it.unit_price_cents) || 0;
                  const lineTotal = q * u;
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-row-${i}`}>
                      <Input placeholder="Description" value={it.description}
                             onChange={(e) => updateItem(i, "description", e.target.value)}
                             className="col-span-12 md:col-span-5" data-testid={`item-desc-${i}`} />
                      <Input type="number" min="0" step="0.01" placeholder="1"
                             value={it.quantity}
                             onChange={(e) => updateItem(i, "quantity", e.target.value)}
                             className="col-span-4 md:col-span-2 text-right" data-testid={`item-qty-${i}`} />
                      <div className="col-span-4 md:col-span-2 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A8A29E]">$</span>
                        <Input type="number" min="0" step="0.01" placeholder="0.00"
                               value={(Number(it.unit_price_cents) / 100).toString()}
                               onChange={(e) => updateItem(i, "unit_price_cents", Math.round(Number(e.target.value || 0) * 100))}
                               className="pl-6 text-right" data-testid={`item-unit-${i}`} />
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right text-sm font-semibold"
                           data-testid={`item-linetotal-${i}`}>{centsToStr(lineTotal)}</div>
                      <button className="col-span-1 text-[#B02222] disabled:opacity-30"
                              onClick={() => removeItem(i)} disabled={form.items.length === 1}
                              title="Remove" data-testid={`item-remove-${i}`}><Trash2 size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start pt-2 border-t border-[#E8E2D9]">
              <div>
                <Label>Tax %</Label>
                <Input type="number" min="0" step="0.01" value={form.tax_percent}
                       onChange={(e) => setForm({ ...form, tax_percent: e.target.value })}
                       className="mt-1.5 max-w-[140px]" data-testid="inv-tax" />
                {Number(form.amount_paid_cents || 0) > 0 && (
                  <p className="text-xs text-[#A8A29E] mt-3">Amount paid updates automatically when a Stripe payment succeeds.</p>
                )}
              </div>
              <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E2D9] p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[#57534E]">Subtotal</span><span data-testid="inv-subtotal">{centsToStr(totals.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-[#57534E]">Tax ({Number(form.tax_percent) || 0}%)</span><span data-testid="inv-taxamt">{centsToStr(totals.tax)}</span></div>
                <div className="flex justify-between text-lg font-display font-bold pt-2 border-t border-[#E8E2D9]">
                  <span>Grand total</span><span data-testid="inv-total">{centsToStr(totals.total)}</span>
                </div>
                {Number(form.amount_paid_cents || 0) > 0 && (
                  <>
                    <div className="flex justify-between text-xs pt-1"><span className="text-[#57534E]">Amount paid</span><span>{centsToStr(Number(form.amount_paid_cents) || 0)}</span></div>
                    <div className="flex justify-between font-semibold pt-1 border-t border-[#E8E2D9]">
                      <span>Balance due</span>
                      <span data-testid="inv-balance">{centsToStr(Math.max(0, totals.total - (Number(form.amount_paid_cents) || 0)))}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <Label>Notes (visible on invoice)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Thank you for your business…" />
            </div>
            <div>
              <Label>Terms</Label>
              <Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-invoice-btn" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
              {saving ? "Saving…" : (editId ? "Save changes" : "Create draft")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
